import type { ClassData, SeatingChart, SeatingAssignment, Student } from "../../types/models";
import { validateProximityConstraints, getWithinGroups, checkAssignmentConstraints, type ConstraintViolation } from "./constraints";
import { greedyPlacement } from "./placement";
import { hillClimbingOptimization, simulatedAnnealingOptimization } from "./optimization";

export interface SeatingProgressHook {
    (label: string, pct: number): void;
}

export interface SeatingGenerationResult {
    assignment: SeatingAssignment;
    violations: ConstraintViolation[];
    warnings: string[];
}

/**
 * Main seating assignment generation pipeline
 *
 * Stages:
 * 0. Validate constraints
 * 1. Get "within" groups (students who must sit together)
 * 2. Greedy placement (place groups first, then individuals)
 * 3. Hill climbing optimization
 * 4. Simulated annealing (escape local minima)
 * 5. Final validation and reporting
 */
export async function generateSeatingAssignment(
    cls: ClassData,
    chart: SeatingChart,
    opts: { onProgress?: SeatingProgressHook } = {}
): Promise<SeatingGenerationResult> {
    const { onProgress } = opts;
    const students = cls.students;

    // Stage 0: Validate constraints
    onProgress?.("Validating constraints...", 0);
    await delay(50);

    const validation = validateProximityConstraints(students, chart);
    if (!validation.ok) {
        throw new Error(validation.errors.join("; "));
    }

    // Stage 1: Get within groups
    onProgress?.("Analyzing proximity constraints...", 15);
    await delay(50);

    const withinGroups = getWithinGroups(chart);

    // Stage 2: Greedy placement
    onProgress?.("Placing students...", 30);
    await delay(50);

    const placementResult = greedyPlacement(students, chart, withinGroups);

    if (placementResult.unplacedStudents.length > 0) {
        const names = placementResult.unplacedStudents
            .map(id => students.find(s => s.id === id)?.name ?? id)
            .join(", ");
        validation.warnings.push(`Could not place: ${names}`);
    }

    // Stage 3: Hill climbing optimization
    onProgress?.("Optimizing seating arrangement...", 50);
    await delay(50);

    let optimizedAssignments = hillClimbingOptimization(
        placementResult.seatAssignments,
        chart,
        students,
        50 // iterations
    );

    // Stage 4: Simulated annealing for better global optimization
    onProgress?.("Fine-tuning placement...", 70);
    await delay(50);

    optimizedAssignments = simulatedAnnealingOptimization(
        optimizedAssignments,
        chart,
        students,
        {
            initialTemp: 50,
            coolingRate: 0.9,
            maxIterations: 200
        }
    );

    // Stage 5: Final hill climbing pass
    onProgress?.("Final optimization pass...", 85);
    await delay(50);

    optimizedAssignments = hillClimbingOptimization(
        optimizedAssignments,
        chart,
        students,
        30
    );

    // Stage 6: Final validation
    onProgress?.("Validating final assignment...", 95);
    await delay(50);

    const finalAssignment: SeatingAssignment = {
        seatAssignments: optimizedAssignments,
        timestamp: Date.now()
    };

    const violations = checkAssignmentConstraints(finalAssignment, chart, students);

    if (violations.length > 0) {
        validation.warnings.push(
            `${violations.length} constraint(s) could not be satisfied`
        );
    }

    onProgress?.("Complete!", 100);

    return {
        assignment: finalAssignment,
        violations,
        warnings: validation.warnings
    };
}

/**
 * Clear all seat assignments in a chart
 */
export function clearAssignments(chart: SeatingChart): SeatingAssignment {
    const seatAssignments = new Map<string, string | undefined>();

    for (const table of chart.tables) {
        for (const seat of table.seats) {
            seatAssignments.set(seat.id, undefined);
        }
    }

    return {
        seatAssignments,
        timestamp: Date.now()
    };
}

/**
 * Manually assign a student to a seat
 */
export function assignStudentToSeat(
    assignment: SeatingAssignment,
    studentId: string,
    seatId: string
): SeatingAssignment {
    const newAssignments = new Map(assignment.seatAssignments);

    // Remove student from any existing seat
    for (const [sid, assignedStudent] of newAssignments) {
        if (assignedStudent === studentId) {
            newAssignments.set(sid, undefined);
        }
    }

    // Assign to new seat
    newAssignments.set(seatId, studentId);

    return {
        seatAssignments: newAssignments,
        timestamp: Date.now()
    };
}

/**
 * Remove a student from their seat
 */
export function unassignStudent(
    assignment: SeatingAssignment,
    studentId: string
): SeatingAssignment {
    const newAssignments = new Map(assignment.seatAssignments);

    for (const [seatId, assignedStudent] of newAssignments) {
        if (assignedStudent === studentId) {
            newAssignments.set(seatId, undefined);
            break;
        }
    }

    return {
        seatAssignments: newAssignments,
        timestamp: Date.now()
    };
}

/**
 * Swap two students' seats
 */
export function swapStudents(
    assignment: SeatingAssignment,
    studentIdA: string,
    studentIdB: string
): SeatingAssignment {
    const newAssignments = new Map(assignment.seatAssignments);

    let seatA: string | null = null;
    let seatB: string | null = null;

    for (const [seatId, studentId] of newAssignments) {
        if (studentId === studentIdA) seatA = seatId;
        if (studentId === studentIdB) seatB = seatId;
    }

    if (seatA && seatB) {
        newAssignments.set(seatA, studentIdB);
        newAssignments.set(seatB, studentIdA);
    }

    return {
        seatAssignments: newAssignments,
        timestamp: Date.now()
    };
}

/** Small delay for progress animation */
function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// Re-export types and utilities
export { validateProximityConstraints, checkAssignmentConstraints } from "./constraints";
export { buildDistanceMatrix } from "./distance";
export type { ConstraintViolation, SeatingValidationResult } from "./constraints";
